import React, { useEffect, useState } from 'react';
import { Repeat, Search, X, Plus, Loader2, Sparkles, Link2 } from 'lucide-react';
import { Input } from './ui/input';
import { adminAPI, productsAPI } from '../services/api';
import BrandLogo from './BrandLogo';

const Row = ({ p, action, testId, note }) => (
  <div className="flex items-center gap-3 px-3 py-2" data-testid={testId}>
    <img src={p.image} alt="" className="w-9 h-9 object-contain bg-gray-50 rounded" />
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-gray-900 truncate">{p.name}</p>
      <p className="text-xs text-gray-500 flex items-center gap-1.5"><BrandLogo brand={p.brand} size="xs" showName={false} /> {p.brand} · Réf. {p.ref} · {Number(p.price).toFixed(2)} €{note && <span className="text-blue-700"> · {note}</span>}</p>
    </div>
    {action}
  </div>
);

const EquivalentsEditor = ({ productId, ids = [], onChange, currentBrand }) => {
  const [linked, setLinked] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [reciprocal, setReciprocal] = useState([]);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!ids.length) { setLinked([]); return; }
    const missing = ids.filter(id => !linked.some(p => p.id === id));
    if (!missing.length) { setLinked(l => ids.map(id => l.find(p => p.id === id)).filter(Boolean)); return; }
    productsAPI.getByIds(ids).then(list => setLinked(ids.map(id => list.find(p => p.id === id)).filter(Boolean))).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids.join(',')]);

  useEffect(() => {
    if (!productId) return;
    adminAPI.equivalentSuggestions(productId).then(async (d) => {
      setSuggestions(d.items);
      if (d.reciprocal_ids.length) setReciprocal(await productsAPI.getByIds(d.reciprocal_ids));
    }).catch(() => {});
  }, [productId]);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults([]); return undefined; }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(() => {
      adminAPI.searchProducts({ q: term, limit: 8 })
        .then(d => { if (!cancelled) setResults(d.items.filter(p => p.id !== productId && !ids.includes(p.id))); })
        .catch(() => {}).finally(() => { if (!cancelled) setSearching(false); });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, productId, ids]);

  const add = (p) => { if (!ids.includes(p.id)) onChange([...ids, p.id]); setQ(''); setResults([]); };
  const remove = (id) => onChange(ids.filter(i => i !== id));
  const pending = suggestions.filter(p => !ids.includes(p.id));

  return (
    <div data-testid="equivalents-editor">
      <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-1"><Repeat size={18} className="text-orange-500" /> Pièces équivalentes (autres marques)</h3>
      <p className="text-xs text-gray-500 mb-3">Liez les produits du catalogue qui sont la même pièce chez une autre marque. Le lien est réciproque : les clients voient les alternatives sur chaque fiche et dans la recherche par référence.</p>

      <div className="relative mb-3">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un produit à lier (nom, référence, marque)…" className="pl-9" data-testid="equivalents-search-input" />
        {searching && <Loader2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-orange-500" />}
        {results.length > 0 && (
          <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-white border rounded-lg shadow-xl divide-y max-h-72 overflow-y-auto" data-testid="equivalents-search-results">
            {results.map(p => <Row key={p.id} p={p} testId={`equivalents-result-${p.id}`} note={p.brand === currentBrand ? 'même marque' : null}
              action={<button type="button" onClick={() => add(p)} className="text-xs font-medium text-white bg-[#1e3a5f] hover:bg-[#2d4a6f] rounded-md px-2.5 py-1 inline-flex items-center gap-1" data-testid={`equivalents-add-${p.id}`}><Plus size={12} /> Lier</button>} />)}
          </div>
        )}
      </div>

      <div className="border rounded-lg divide-y" data-testid="equivalents-linked-list">
        {linked.length === 0 && reciprocal.length === 0 && <p className="px-3 py-4 text-sm text-gray-400 text-center">Aucune pièce équivalente liée.</p>}
        {linked.map(p => <Row key={p.id} p={p} testId={`equivalents-linked-${p.id}`}
          action={<button type="button" onClick={() => remove(p.id)} className="p-1.5 text-gray-400 hover:text-red-600" title="Retirer le lien" data-testid={`equivalents-remove-${p.id}`}><X size={14} /></button>} />)}
        {reciprocal.filter(p => !ids.includes(p.id)).map(p => <Row key={p.id} p={p} testId={`equivalents-reciprocal-${p.id}`} note="lié depuis sa fiche"
          action={<span className="text-[11px] text-gray-400 inline-flex items-center gap-1"><Link2 size={12} /> réciproque</span>} />)}
      </div>

      {pending.length > 0 && (
        <div className="mt-3 bg-orange-50/60 border border-orange-100 rounded-lg" data-testid="equivalents-suggestions">
          <p className="px-3 pt-2 text-xs font-medium text-orange-800 flex items-center gap-1.5"><Sparkles size={13} /> Suggestions : même référence OEM, pas encore liées</p>
          <div className="divide-y">
            {pending.map(p => <Row key={p.id} p={p} testId={`equivalents-suggestion-${p.id}`}
              action={<button type="button" onClick={() => add(p)} className="text-xs font-medium text-orange-700 border border-orange-300 hover:bg-orange-100 rounded-md px-2.5 py-1 inline-flex items-center gap-1" data-testid={`equivalents-link-suggestion-${p.id}`}><Plus size={12} /> Lier</button>} />)}
          </div>
        </div>
      )}
    </div>
  );
};

export default EquivalentsEditor;
